import { getLocalTimeZone, today, CalendarDate } from '@internationalized/date';
import { enumServiceStatus } from '@rematter/frontend-lib-api-client';
import { atom } from 'jotai';

import { ServiceRecord, TransportationModal } from '../types';

export const $selectedDate = atom<CalendarDate>(today(getLocalTimeZone()));
export const $activeTab = atom<'requests' | 'unassigned'>('unassigned');
export const $pinnedColumnCollapsed = atom(false);
export const $searchTerm = atom('');

export const $visibleModal = atom<TransportationModal | undefined>(undefined);

export interface PendingAssignment {
  driverId: string;
  rank: string;
  error?: boolean;
}

const $pendingAssignmentsBase = atom<Map<string, PendingAssignment>>(new Map());

export const $pendingAssignments = atom((get) => get($pendingAssignmentsBase));

export const $addPendingAssignment = atom(
  undefined,
  (get, set, params: { serviceId: string; driverId: string; rank: string }) => {
    const next = new Map(get($pendingAssignmentsBase));
    next.set(params.serviceId, {
      driverId: params.driverId,
      rank: params.rank,
    });
    set($pendingAssignmentsBase, next);
  },
);

export const $removePendingAssignment = atom(
  undefined,
  (get, set, serviceId: string) => {
    const next = new Map(get($pendingAssignmentsBase));
    next.delete(serviceId);
    set($pendingAssignmentsBase, next);
  },
);

export const $clearAllPending = atom(undefined, (_, set) => {
  set($pendingAssignmentsBase, new Map());
});

export const $pendingCountByDriver = atom((get) => {
  const counts = new Map<string, number>();
  for (const [, assignment] of get($pendingAssignmentsBase)) {
    counts.set(assignment.driverId, (counts.get(assignment.driverId) ?? 0) + 1);
  }
  return counts;
});

export const $pendingConflicts = atom<string[]>([]);

export const $clearPendingConflicts = atom(undefined, (_, set) => {
  set($pendingConflicts, []);
});

export const $markPendingError = atom(
  undefined,
  (get, set, serviceId: string) => {
    const next = new Map(get($pendingAssignmentsBase));
    const entry = next.get(serviceId);
    if (entry) {
      next.set(serviceId, { ...entry, error: true });
      set($pendingAssignmentsBase, next);
    }
  },
);

function mergeServiceWithPending(
  service: ServiceRecord,
  pending: Map<string, PendingAssignment>,
): ServiceRecord {
  const assignment = pending.get(service.id);
  if (!assignment) return service;
  return {
    ...service,
    driverId: assignment.driverId,
    rank: assignment.rank,
  };
}

const $localServices = atom<ServiceRecord[]>([]);

export const $getLocalServices = atom((get) => get($localServices));

export const $setLocalServices = atom(
  undefined,
  (get, set, services: ServiceRecord[]) => {
    const pending = get($pendingAssignmentsBase);
    if (pending.size === 0) {
      set($localServices, services);
      return;
    }

    const nextPending = new Map(pending);
    const conflicts: string[] = [];

    const merged = services.map((service) => {
      if (!nextPending.has(service.id)) return service;

      if (service.status !== enumServiceStatus.Draft) {
        nextPending.delete(service.id);
        conflicts.push(service.id);
        return service;
      }

      return mergeServiceWithPending(service, nextPending);
    });

    set($localServices, merged);

    if (nextPending.size !== pending.size) {
      set($pendingAssignmentsBase, nextPending);
    }
    if (conflicts.length > 0) {
      set($pendingConflicts, [...get($pendingConflicts), ...conflicts]);
    }
  },
);

export const $replaceLocalService = atom(
  undefined,
  (get, set, service: ServiceRecord) => {
    const pending = get($pendingAssignmentsBase);
    const assignment = pending.get(service.id);

    if (assignment) {
      if (service.status !== enumServiceStatus.Draft) {
        const nextPending = new Map(pending);
        nextPending.delete(service.id);
        set($pendingAssignmentsBase, nextPending);
        set($pendingConflicts, [...get($pendingConflicts), service.id]);
      } else {
        const existingService = get($localServices).find(
          (it) => it.id === service.id,
        );
        if (
          existingService?.timestamps?.updatedAt &&
          service.timestamps?.updatedAt &&
          existingService.timestamps.updatedAt !== service.timestamps.updatedAt
        ) {
          set($pendingConflicts, [...get($pendingConflicts), service.id]);
        }
        service = mergeServiceWithPending(service, pending);
      }
    }

    set($localServices, [
      ...get($localServices).filter((it) => it.id !== service.id),
      service,
    ]);
  },
);

export const $assignDriverToLocalService = atom(
  undefined,
  (
    get,
    set,
    params: {
      id: string;
      driverId: string;
      truckId?: string;
      trailerId?: string;
      rank: string;
    },
  ) => {
    set($localServices, [
      ...get($localServices).map((it) =>
        it.id === params.id
          ? {
              ...it,
              driverId: params.driverId,
              truckId: params.truckId,
              trailerId: params.trailerId,
              rank: params.rank ?? it.rank,
            }
          : it,
      ),
    ]);
  },
);

export const $unassignDriverFromLocalService = atom(
  undefined,
  (
    get,
    set,
    params: {
      id: string;
      rank: string;
    },
  ) => {
    set($localServices, [
      ...get($localServices).map((it) =>
        it.id === params.id
          ? {
              ...it,
              driverId: undefined,
              trailerId: undefined,
              rank: params.rank ?? it.rank,
            }
          : it,
      ),
    ]);
  },
);

export const $updateLocalServicePositions = atom(
  undefined,
  (get, set, params: { routeId: string; orderedServiceIds: string[] }) => {
    set(
      $localServices,
      get($localServices).map((service) => {
        if (service.routeId !== params.routeId) return service;
        const position = params.orderedServiceIds.indexOf(service.id);
        if (position === -1) return service;
        return { ...service, routePosition: position };
      }),
    );
  },
);
